import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { cssHexToWindowsColorRefArgb, uniteColorFieldToColorBoxValue } from '../../utils/windowsColorRef';
import './couleur.css';

type IUseCouleurInputParametres<T> = {
  champ: keyof T & string,
  dataRow: T | undefined,
  setDataRow: Dispatch<SetStateAction<T>>,
  setIsFieldModifier: (isFieldModifier: boolean) => void,
  isModeModifier: boolean,
  classeCss?: string,
  maxWidth?: number | string,
}

function useCouleurInput<T>({
  champ,
  dataRow,
  setDataRow,
  setIsFieldModifier,
  isModeModifier,
  classeCss = 'gestion-unite-colorbox',
  maxWidth,
}: IUseCouleurInputParametres<T>) {

  const onValueChanged = useCallback((e: { value?: string }) => {
    const valeurEntier = e.value ? cssHexToWindowsColorRefArgb(String(e.value)) : 0;
    setDataRow((prev: T) => {
      if (!prev || prev[champ] === (valeurEntier as unknown as T[keyof T])) {
        return prev;
      }
      return { ...prev, [champ]: valeurEntier };
    });
    if (isModeModifier) {
      setIsFieldModifier(true);
    }
  }, [champ, setDataRow, setIsFieldModifier, isModeModifier]);

  const couleurOptions = useMemo(() => {
    const classes = [classeCss, maxWidth !== undefined ? 'gestion-unite-colorbox--max-width' : ''].filter(Boolean);
    const elementAttr: { class?: string, style?: string } = {};

    if (classes.length > 0) {
      elementAttr.class = classes.join(' ');
    }

    if (maxWidth !== undefined) {
      const maxWidthValue = typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth;
      elementAttr.style = `--couleur-input-max-width: ${maxWidthValue};`;
    }

    return {
      ...(maxWidth !== undefined ? { width: '100%' } : {}),
      elementAttr: Object.keys(elementAttr).length > 0 ? elementAttr : undefined,
      readOnly: !isModeModifier,
      value: uniteColorFieldToColorBoxValue(dataRow?.[champ]) || undefined,
      onValueChanged,
    };
  }, [classeCss, maxWidth, isModeModifier, dataRow, champ, onValueChanged]);

  return { couleurOptions };
}

export default useCouleurInput;
